import { useMemo } from "react";

import type { ChartTableSelection } from "../../lib/chartTables.js";
import { SHARED_FULL_WEEKS_CRITERIA, SHARED_TABLES_CRITERIA } from "../../lib/chartTables.js";
import { selectedDayUptime, sumDeviceDayCount } from "../../lib/chartTableSeries.js";
import {
  dailyChartBarAxisProps,
  dailyChartRotateAxisLabels,
  formatDashboardDayLabelLong,
} from "../../lib/dashboardDayLabel.js";
import {
  formatWeekAxisLabel,
  FULL_WEEKS_EMPTY_MESSAGE,
  groupFullMondayWeeks,
  shiftReportingDayKey,
  type UptimeWeekBucket,
} from "../../lib/uptimeWeekBuckets.js";
import { useSharedChartTables } from "../../lib/useSharedChartTables.js";
import type { AcquisitionData, DashboardTabHandler, FilterState } from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import type { BarChartItem } from "../charts/BarChart.js";
import { BarChart } from "../charts/BarChart.js";
import { ChartTablePicker } from "./ChartTablePicker.js";

/** Same purple as Payment Flow Funnel “Interaction Stints”. */
const INTERACTION_STINTS_FILL = "#8b7cf6";

/** Above this, rate labels on the bar tops overlap and are omitted. */
const VALUE_LABEL_MAX_BARS = 7;

type StintDay = AcquisitionData["interactionStintsPerUptimeHourByDay"][number];

function formatRate(rate: number): string {
  return rate < 10 ? rate.toFixed(2) : rate.toFixed(1);
}

/** Plot whenever there is uptime — unmeasured days still get a bar (weeks already did). */
function dayRate(row: Pick<StintDay, "stintCount" | "uptimeHours">): number | null {
  return row.uptimeHours > 0 ? row.stintCount / row.uptimeHours : null;
}

function weekTotals(
  week: UptimeWeekBucket,
  byDay: ReadonlyMap<string, StintDay>,
): { stintCount: number; rate: number | null } {
  let stintCount = 0;
  for (let i = 0; i < 7; i++) {
    const day = shiftReportingDayKey(week.monday, i);
    stintCount += (day && byDay.get(day)?.stintCount) || 0;
  }
  // Same as Revenue by day weeks: still plot summed stints ÷ summed uptime when a day was
  // unmeasured. Nulling the rate hid every bar (value 0) for ranges that include rollout days.
  const rate = week.uptimeHours > 0 ? stintCount / week.uptimeHours : null;
  return { stintCount, rate };
}

const CRITERIA = `Numerator: same as **Interaction Stints (by day)** — distinct non-empty **interaction_session_id**, bucketed by reporting day of each stint’s first event in the window.

Denominator: fleet-additive **uptime hours** for all tables in the venue/device filter (same Health Day Capacity Mix definition: platform-observed heartbeat minutes, excluding declared scheduled windows). Hours are summed across devices (tables × day), so a day with two tables up for 12h each contributes 24h.

Bar = stints ÷ uptime hours that day. Days with 0 uptime hours show **—** (value 0). If the day has any time before heartbeat collection started, the tooltip says so; the rate is still plotted from measured uptime (same as Full weeks / Revenue by day). Game filter applies to stints only (uptime ignores Game, same as Health).

**Full weeks** (checkbox, off by default; shared URL \`weeks=1\`): one bar per complete **Monday–Sunday** whose seven days are all inside the filter window and each have a full day's capacity (today-so-far and future days do not count — same rule as Health Uptime by day). Stints and uptime hours are **summed**; the bar is that total ÷ total uptime. Rate is **—** only when the week has no uptime. If a day was not fully measured, the tooltip says so; the rate is still plotted (same as Revenue by day). Partial weeks at the edges are omitted.

**Unique tables** = named devices in the current venue/device scope (Health **tableCount**), or the tables checked in the shared dropdown.

${SHARED_TABLES_CRITERIA}

${SHARED_FULL_WEEKS_CRITERIA}

Value labels above bars are omitted when there are more than 7 days or weeks. Bar click: Interaction sessions tab, date range narrowed to that day (or that Monday–Sunday in Full weeks).`;

export function StintsPerUptimeHourCard({
  rows: fleetRows,
  tableCount: fleetTableCount,
  devices,
  uptimeByDeviceDay,
  stintsByDeviceDay,
  chartTables,
  onChartTablesChange,
  fullWeeks,
  onFullWeeksChange,
  filters,
  onOpenSessions,
}: {
  rows: StintDay[];
  tableCount: number;
  devices: AcquisitionData["chartDevices"];
  uptimeByDeviceDay: AcquisitionData["uptimeByDeviceDay"];
  stintsByDeviceDay: AcquisitionData["stintsByDeviceDay"];
  chartTables: ChartTableSelection;
  onChartTablesChange: (next: ChartTableSelection) => void;
  fullWeeks: boolean;
  onFullWeeksChange: (next: boolean) => void;
  filters: FilterState;
  onOpenSessions: DashboardTabHandler;
}) {
  const tables = useSharedChartTables(devices, chartTables, onChartTablesChange, filters);
  const stintCounts = useMemo(
    () => stintsByDeviceDay.map((row) => ({ ...row, count: row.stintCount })),
    [stintsByDeviceDay],
  );
  const rows = useMemo(() => {
    if (tables.useFleetSeries) {
      return fleetRows.map((row) => ({
        ...row,
        stintsPerUptimeHour: dayRate(row),
      }));
    }
    if (tables.effectiveIds.size === 0) return [];
    return fleetRows.map((row) => {
      const up = selectedDayUptime(uptimeByDeviceDay, tables.effectiveIds, row.day);
      const stintCount = sumDeviceDayCount(stintCounts, tables.effectiveIds, row.day);
      return {
        day: row.day,
        stintCount,
        uptimeHours: up.uptimeHours,
        capacityHours: up.capacityHours,
        unmeasuredHours: up.unmeasuredHours,
        stintsPerUptimeHour: dayRate({ stintCount, uptimeHours: up.uptimeHours }),
      };
    });
  }, [fleetRows, stintCounts, tables.effectiveIds, tables.useFleetSeries, uptimeByDeviceDay]);
  const tableCount = tables.useFleetSeries ? fleetTableCount : tables.effectiveIds.size;
  const byDay = useMemo(() => new Map(rows.map((row) => [row.day, row])), [rows]);

  const weeks = useMemo(() => {
    if (!fullWeeks || tableCount <= 0) return [];
    return groupFullMondayWeeks(
      rows.map((row) => ({
        day: row.day,
        uptimeHours: row.uptimeHours,
        scheduledHours: 0,
        capacityHours: row.capacityHours,
        unmeasuredHours: row.unmeasuredHours,
        expectedHours: 0,
      })),
      tableCount,
    );
  }, [fullWeeks, rows, tableCount]);

  const bars: BarChartItem[] = useMemo(() => {
    if (fullWeeks) {
      return weeks.map((week) => {
        const { rate } = weekTotals(week, byDay);
        return {
          label: formatWeekAxisLabel(week.monday, week.sunday),
          value: rate ?? 0,
          valueLabel: rate == null ? "—" : formatRate(rate),
          fill: INTERACTION_STINTS_FILL,
        };
      });
    }
    return rows.map((row) => {
      const rate = row.stintsPerUptimeHour;
      return {
        ...dailyChartBarAxisProps(row.day),
        value: rate ?? 0,
        valueLabel: rate == null ? "—" : formatRate(rate),
        fill: INTERACTION_STINTS_FILL,
      };
    });
  }, [byDay, fullWeeks, rows, weeks]);

  const shownStints = fullWeeks
    ? weeks.reduce((sum, week) => sum + weekTotals(week, byDay).stintCount, 0)
    : rows.reduce((sum, row) => sum + row.stintCount, 0);
  const shownUptimeHours = fullWeeks
    ? weeks.reduce((sum, week) => sum + week.uptimeHours, 0)
    : rows.reduce((sum, row) => sum + row.uptimeHours, 0);
  const overallRate = shownUptimeHours > 0 ? shownStints / shownUptimeHours : null;
  const showValueLabels = bars.length > 0 && bars.length <= VALUE_LABEL_MAX_BARS;

  return (
    <ChartCard
      title="Interaction Stints / Uptime Hour"
      titleRight={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] font-medium text-text-primary">
            <input
              type="checkbox"
              className="accent-accent-2"
              checked={fullWeeks}
              onChange={(e) => onFullWeeksChange(e.target.checked)}
            />
            Full weeks
          </label>
          <span className="shrink-0 text-[12px] font-medium tabular-nums text-text-secondary">
            {tableCount.toLocaleString()} unique table{tableCount === 1 ? "" : "s"}
            {overallRate != null ? ` · ${formatRate(overallRate)} /h overall` : ""}
          </span>
        </div>
      }
      criteria={CRITERIA}
      note={
        fullWeeks
          ? "One bar per complete Monday–Sunday in the range. Partial weeks (including this week if it has not finished) are omitted. Purple matches Interaction Stints."
          : "Normalises visitor-stint volume by how long tables were up that day across the filtered fleet. Purple matches Interaction Stints."
      }
    >
      <div className="mb-1 flex justify-end">
        <ChartTablePicker
          devices={devices}
          selectedIds={tables.effectiveIds}
          onSelectedIdsChange={tables.onSelectedIdsChange}
          locked={tables.filterLocksSingle}
        />
      </div>
      {!tables.useFleetSeries && tables.effectiveIds.size === 0 ? (
        <p className="py-5 text-[13px] text-text-secondary">Select at least one table.</p>
      ) : fullWeeks && bars.length === 0 ? (
        <p className="py-5 text-[13px] text-text-secondary">
          {FULL_WEEKS_EMPTY_MESSAGE}
        </p>
      ) : (
        <BarChart
          data={bars}
          fillBars
          compact={dailyChartRotateAxisLabels(bars.length)}
          verbatimAxisLabels
          rotateAxisLabels={dailyChartRotateAxisLabels(bars.length)}
          axisLabelMaxChars={fullWeeks ? 12 : 10}
          showYAxis
          showValueLabels={showValueLabels}
          barTooltip={(item, index) => {
            if (fullWeeks) {
              const week = weeks[index];
              if (!week) return item.label;
              const { stintCount, rate } = weekTotals(week, byDay);
              const rateBit = rate == null ? "—" : formatRate(rate);
              const unmeasuredBit =
                week.unmeasuredHours > 0
                  ? " A day in the week was not fully measured; rate is still week stints ÷ week uptime."
                  : "";
              return `${formatDashboardDayLabelLong(week.monday)} – ${formatDashboardDayLabelLong(week.sunday)}: ${rateBit} stints/uptime-h (${stintCount.toLocaleString()} stints ÷ ${week.uptimeHours.toFixed(1)}h fleet uptime across ${tableCount} unique table${tableCount === 1 ? "" : "s"}).${unmeasuredBit} Click: Sessions for this week. Middle-click / Ctrl/Cmd+click: new tab.`;
            }
            const row = rows[index];
            if (!row) return item.label;
            const rate = row.stintsPerUptimeHour == null ? "—" : formatRate(row.stintsPerUptimeHour);
            const unmeasuredBit =
              row.unmeasuredHours > 0
                ? " Day was not fully measured before heartbeat collection; rate is still stints ÷ measured uptime."
                : "";
            return `${formatDashboardDayLabelLong(row.day)}: ${rate} stints/uptime-h (${row.stintCount.toLocaleString()} stints ÷ ${row.uptimeHours.toFixed(1)}h fleet uptime across ${tableCount} unique table${tableCount === 1 ? "" : "s"}).${unmeasuredBit} Click: Sessions for this day. Middle-click / Ctrl/Cmd+click: new tab.`;
          }}
          onBarClick={(_item, index, _si, e) => {
            if (fullWeeks) {
              const week = weeks[index];
              if (!week) return;
              onOpenSessions(e, { drillDownFrom: week.monday, drillDownTo: week.sunday });
              return;
            }
            const row = rows[index];
            if (!row?.day) return;
            onOpenSessions(e, { drillDownFrom: row.day, drillDownTo: row.day });
          }}
        />
      )}
    </ChartCard>
  );
}
